import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  InputAdornment, 
  Grid, 
  Menu, 
  MenuItem, 
  Typography, 
  Tooltip 
} from '@mui/material';
import { SearchIcon, SortAscIcon, SortDescIcon } from 'components/SVG';
import { usePopover } from 'hooks/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SparkRunsStatusType } from 'types/entityTypes/SparkRunsStatusTypes';
import { showRunsSparkStatus } from 'components/Dashboard/HelperFunctions/showRecentSparkStatuses';

// GUID regular expression pattern
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Function to validate if a string is a valid GUID
const isValidGuid = (value: string): boolean => {
  return GUID_REGEX.test(value);
};

type FilterDropdownProps = {
  title: string;
  options: { label: string; value: string; attributes?: any }[];
  selectedValue: string;
  onSelect: (value: string) => void;
};

export const FilterDropdown: React.FC<FilterDropdownProps> = ({ 
  title, 
  options, 
  selectedValue, 
  onSelect 
}) => {
  const { open, openPopover, anchorEl, closePopover } = usePopover();

  const handleSelect = (value: string) => {
    onSelect(value);
    closePopover();
  };

  const displayValue = options.find(option => option.value === selectedValue)?.label || title;
  const isFilterApplied = selectedValue !== 'all';

  return (
    <Box width="100%">
      <Box
        bgcolor="white"
        height="2.5rem"
        onClick={openPopover}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderRadius="10px"
        border={(theme: any) => `1px solid ${theme.palette.borderColor || '#EEE'}`}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            borderColor: (theme: any) => theme.palette.primary.main,
          },
          padding: '0',
          boxSizing: 'border-box',
        }}
      >
        <Box width="100%" padding={1}>
          {!isFilterApplied && (
            <Typography variant="body2" fontFamily="Mont-semibold">
              {title}
            </Typography>
          )}
          {isFilterApplied && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: (theme: any) => theme.palette.labelColor || theme.palette.grey[500] }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  wordBreak: 'break-word',
                  fontFamily: 'Mont-semibold',
                }}
              >
                {displayValue}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{ cursor: 'pointer' }}
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingRight={1}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      <Menu
        open={open}
        onClose={closePopover}
        anchorEl={anchorEl}
        sx={{ 
          zIndex: 9999,
          '& .MuiPaper-root': {
            maxHeight: 300,
            width: anchorEl ? (anchorEl as HTMLElement).offsetWidth : 'auto',
            marginTop: '5px',
            color: '#333333',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #EEEEEE'
          },
          '& ul': { 
            paddingBottom: 0,
            maxHeight: 250,
            overflowY: 'auto'
          } 
        }}
        MenuListProps={{
          dense: true
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
            selected={option.value === selectedValue}
            sx={{ 
              minWidth: '200px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '10px 16px',
              color: '#333333',
              '&.Mui-selected': {
                backgroundColor: theme => theme.palette.primary.light,
              },
              '&:hover': {
                backgroundColor: theme => theme.palette.grey[100],
              }
            }}
          >
            {option.attributes && (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: option.attributes.textColour || '#333333'
                }}
              >
                {option.attributes.iconURL && (
                  <img 
                    src={option.attributes.iconURL} 
                    alt={option.attributes.name || ''} 
                    style={{ width: '24px', height: '24px' }}
                  />
                )}
                <Typography 
                  variant="body2" 
                  fontFamily="Mont-semibold"
                  sx={{ 
                    color: option.attributes.textColour || '#333333'
                  }}
                >
                  {option.attributes.name || option.label}
                </Typography>
              </Box>
            )}
            {!option.attributes && (
              <Typography 
                variant="body2" 
                fontFamily="Mont-semibold"
                sx={{ color: '#333333' }}
              >
                {option.label}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

type SparkRunsFiltersProps = {
  statusTypes?: SparkRunsStatusType[];
  selectedStatus: string;
  onStatusChange: (statusID: string) => void;
  onRunIdChange: (runId: string) => void;
  runIdValue: string;
};

const SparkRunsFilters: React.FC<SparkRunsFiltersProps> = ({
  statusTypes,
  selectedStatus,
  onStatusChange,
  onRunIdChange,
  runIdValue
}) => {
  // Internal state for the input value
  const [inputValue, setInputValue] = useState<string>(runIdValue);
  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>('');

  // Handle input change
  const handleRunIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear validation when empty
    if (!newValue) {
      setError(false);
      setHelperText('');
      onRunIdChange(''); // Clear the filter
      return;
    }

    // Validate GUID format
    if (isValidGuid(newValue)) {
      setError(false);
      setHelperText('');
      onRunIdChange(newValue); // Apply the filter with valid GUID
    } else {
      setError(true);
      setHelperText('Enter a valid Run ID');
      onRunIdChange(''); // Don't apply an invalid filter
    }
  };

  // Prepare options for status dropdown
  const statusOptions = React.useMemo(() => {
    const allOption = { label: 'Any Status', value: 'all' };
    
    if (!statusTypes) return [allOption];
    
    const typeOptions = statusTypes.map(type => ({
      label: type.attributes.name,
      value: type.id,
      attributes: type.attributes
    }));
    
    return [allOption, ...typeOptions];
  }, [statusTypes]);

  return (
    <Grid container spacing={2} sx={{ mb: 3, px: 0 }}>
      <Grid item xs={12} md={6}>
        <Box sx={{ px: 0, height: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <TextField
            placeholder="Enter Run ID"
            size="small"
            fullWidth
            value={inputValue}
            onChange={handleRunIdChange}
            error={error}
            helperText={helperText}
            inputProps={{
              style: {
                fontSize: '14px', // Smaller font size for the placeholder and input text
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: '#FFFFFF',
              '& .MuiOutlinedInput-root': {
                height: '2.5rem',
                borderRadius: '10px',
                border: (theme: any) => `1px solid ${theme.palette.borderColor || '#EEE'}`,
                display: 'flex',
                alignItems: 'center',
                padding: '0', // Remove default padding
                boxSizing: 'border-box',
                '& input': {
                  padding: '0 14px 0 0', // Adjust padding only for the input element
                  height: '100%',
                },
                '& .MuiInputAdornment-root': {
                  height: '100%',
                  margin: '0',
                  padding: '0 8px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
                '&:hover': {
                  border: (theme: any) => `1px solid ${theme.palette.primary.main}`,
                },
                '&.Mui-error': {
                  border: '1px solid #d32f2f',
                }
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                bottom: '-20px',
                margin: 0,
                fontSize: '0.7rem'
              },
              marginTop: 0, // Ensure no top margin
              marginBottom: 0, // Ensure no bottom margin
              height: '2.5rem' // Explicit height for the whole component
            }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ px: 0, height: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <FilterDropdown
            title="Status"
            options={statusOptions}
            selectedValue={selectedStatus}
            onSelect={onStatusChange}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default SparkRunsFilters; 